import React, { useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import _, { round } from "lodash";
import { Gauge } from "@ant-design/plots";
import { Card, Col, Collapse, Row, Skeleton } from "antd";
import {
  ChevronUp,
  ChevronDown,
  UserPlus,
  RefreshCw,
  Trophy,
  CircleDollarSign,
  PieChart,
  ThumbsDown,
} from "lucide-react";

import {
  getTopClosedOpportunity,
  getTopOpenOpportunity,
  getGroupedCountData,
  getSalesTrendData,
} from "../../actionCreator/dashboardActionCreator";
import { getUserInfo } from "../../utils/utils";
import { formatCompactIndian } from "../../utils/formatCompactIndian";
import ChartRenderer from "./ChartRenderer";
import leadGenerated from "../../assets/svg/icons/lead-generated.svg";
import refresh from "../../assets/svg/icons/refresh.svg";
import wonRatio from "../../assets/svg/icons/won-ratio.svg";
import pipeline from "../../assets/svg/icons/pipeline.svg";
import thumbsDown from "../../assets/svg/icons/thumbs-down.svg";
import seprator from "../../assets/svg/icons/seprator.svg";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertProvider";

const getSentimentColor = ({ sentiment }) => {
  if (sentiment === "High") return "#5D44FF";
  if (sentiment === "Medium") return "#8C4BAA";
  return "#559BA3";
};

const LeadReport = ({ userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = React.useState(false);
  const requestRef = useRef(0);

  const isMyDashboard = user?.id == userId;
  const closedOpprtunityData = useSelector(
    (state) => state.DashboardReducer.closedOpp,
  );
  const openOpprtunityData = useSelector(
    (state) => state.DashboardReducer.openOpp,
  );
  const groupedCountData = useSelector(
    (state) => state.DashboardReducer.groupedCountData,
  );
  const salesTrendData = useSelector(
    (state) => state.DashboardReducer.salesTrendData,
  );
  useEffect(() => {
    if (Object.keys(getUserInfo())?.length) {
      const payload = { userId };
      const requestId = ++requestRef.current;
      setIsLoading(true);
      Promise.all([
        dispatch(getTopClosedOpportunity(payload)),
        dispatch(getTopOpenOpportunity(payload)),
        dispatch(getGroupedCountData(payload)),
        dispatch(getSalesTrendData(payload)),
      ]).finally(() => {
        if (requestId === requestRef.current) {
          setIsLoading(false);
        }
      });
    }
  }, [dispatch, userId]);

  const handleRedirection = (path) => {
    if (!isMyDashboard) {
      showAlert(
        "info",
        "Redirection is not allowed when viewing another user's dashboard.",
      );
      return;
    }
    navigate(path);
  };

  let leadConversionValue = 0;
  let opportunityWonValue = 0;
  let leadConversionRate = 0;
  let opportunityWonRatio = 0;
  let lostOpportunity = 0;
  let salesOrderByStatus = [{ typeValue: "", countSum: 0 }];
  let salesTrendWonData = [];
  let salesTrendWonDataByMonth = [];
  const wonStatus = "Closed Won";
  const lostStatus = "Closed Lost";
  const unQualifiedStatus = "Un-Qualified";
  const qualifiedStatus = "Sales Qualified";
  const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  if (salesTrendData?.dashboardDTOS?.length > 0) {
    salesTrendData?.dashboardDTOS?.forEach((record) => {
      record.month = record?.typeValue ? monthNames[record.typeValue] : "";
      record.opportunitSum =
        record?.opportunitSum && record?.opportunitSum !== "null"
          ? parseFloat(record?.opportunitSum)
          : 0;
      salesTrendWonData.push(record);
    });
    if (salesTrendWonData) {
      salesTrendWonDataByMonth = _.map(
        _.groupBy(salesTrendWonData, "typeValue"),
        (o, month) => {
          return {
            month: month ? monthNames[month] : "",
            opportunitSum: _.sumBy(o, function (record) {
              return record?.statusId === "16" ? record?.opportunitSum : 0;
            }),
          };
        },
      );
    }
  }
  if (groupedCountData?.dashboardDTOS?.length > 0) {
    groupedCountData?.dashboardDTOS?.forEach((record) => {
      record.countSum = record?.countSum ? parseInt(record.countSum) : 0;
      if (record?.typeValue === qualifiedStatus) {
        leadConversionValue += Number(record.countSum);
      }
      record.opportunitSum =
        record.opportunitSum === "null" ? 0 : record.opportunitSum;
      if (record?.typeValue === wonStatus) {
        opportunityWonValue += Number(record.countSum);
      }
      if (
        record?.typeValue === unQualifiedStatus ||
        record?.typeValue === lostStatus
      ) {
        lostOpportunity += Number(record.opportunitSum);
      }
    });
    let rawData = groupedCountData?.dashboardDTOS;
    salesOrderByStatus = _.orderBy(rawData, "countSum", "desc");
  }
  if (groupedCountData?.leadGenareatedCount > 0) {
    leadConversionRate = round(
      (leadConversionValue / groupedCountData?.leadGenareatedCount) * 100,
      2,
    );
    opportunityWonRatio = round(
      (opportunityWonValue / groupedCountData?.leadGenareatedCount) * 100,
      2,
    );
  }

  let totalOpportunity = groupedCountData?.totalOpportunity; //50;
  let totalValueRate = groupedCountData?.totalValueRate; //100;
  let salesPerc = 0;
  let meterPerc = 0;
  if (totalValueRate > 0) {
    salesPerc = round(totalOpportunity / totalValueRate, 2);
    meterPerc = salesPerc / 100;
  }
  salesPerc = Math.max(0, Math.min(salesPerc, 100));
  meterPerc = Math.max(0, Math.min(meterPerc, 1));

  const closedSalesAmount = Number(totalOpportunity || 0).toLocaleString(
    "en-US",
    {
      maximumFractionDigits: 0,
    },
  );

  const chart2 = {
    percent: meterPerc,

    startAngle: Math.PI,
    endAngle: 2 * Math.PI,

    autoFit: true,
    radius: 0.9,
    innerRadius: 0.85,

    range: {
      ticks: [0, meterPerc, 1],
      color: ["l(0) 0:#6B5BFF 1:#4E39F6", "#E5EAFC"],
    },

    gaugeStyle: {
      lineCap: "round",
    },

    meter: {
      steps: 1,
    },

    indicator: null,

    axis: {
      line: null,
      label: null,
      tickLine: null,
      subTickLine: null,
    },

    statistic: {
      title: false,
      content: {
        offsetY: -10,
        customHtml: () => `
        <div style="text-align:center;">
          <div style="font-size:42px;font-weight:700;color:#2E2C27;">
            ${salesPerc}%
          </div>
          <div style="margin-top:20px;font-size:14px;color:#2E2C27;">
            Sales:
            <span style="font-weight:700;color:#2E2C27;">
              \u20B9${closedSalesAmount} 
            </span>
          </div>
        </div>
      `,
      },
    },
  };

  return (
    <div className="tw-bg-[#f8fcff] tw-p-5">
      <div className="tw-mb-6">
        <Collapse
          expandIcon={({ isActive }) =>
            isActive ? <ChevronUp className="tw-w-4 tw-h-4" /> : <ChevronDown className="tw-w-4 tw-h-4" />
          }
          className="tw-advanced-filter"
          expandIconPosition="end"
          items={[
            {
              label: (
                <div className="tw-text-base tw-font-semibold">
                  Revenue & Pipeline Health
                </div>
              ),
              children: (
                <Skeleton active loading={isLoading} paragraph={{ rows: 2 }}>
                  <Row wrap={false} align="middle">
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() => handleRedirection(`/search-lead`)}
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <UserPlus className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {groupedCountData?.leadGenareatedCount}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Lead Generated
                      </div>
                    </div>
                  </Col>
                  <Col flex="24px" className="tw-flex tw-justify-center">
                    <img src={seprator} alt="" />
                  </Col>
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=13`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <RefreshCw className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {leadConversionRate}%
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Lead Conversion Rate
                      </div>
                    </div>
                  </Col>
                  <Col flex="24px" className="tw-flex tw-justify-center">
                    <img src={seprator} alt="" />
                  </Col>
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=16`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <Trophy className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {opportunityWonRatio}%
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Opportunity Won Ratio
                      </div>
                    </div>
                  </Col>
                  <Col flex="24px" className="tw-flex tw-justify-center">
                    <img src={seprator} alt="" />
                  </Col>
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=14`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <CircleDollarSign className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {formatCompactIndian(
                          groupedCountData?.leadOpenOpportunity,
                          {
                            currency: true,
                          },
                        )}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Open Opportunities
                      </div>
                    </div>
                  </Col>
                  <Col flex="24px" className="tw-flex tw-justify-center">
                    <img src={seprator} alt="" />
                  </Col>
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=14`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <PieChart className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {formatCompactIndian(
                          groupedCountData?.totalOpportunity,
                          {
                            currency: true,
                          },
                        )}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Pipeline Contribution
                      </div>
                    </div>
                  </Col>
                  <Col flex="24px" className="tw-flex tw-justify-center">
                    <img src={seprator} alt="" />
                  </Col>
                  <Col flex="1 1 0">
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1 tw-px-2"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=17`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <ThumbsDown className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {formatCompactIndian(lostOpportunity, {
                          currency: true,
                        })}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Lost Opportunities
                      </div>
                    </div>
                  </Col>
                  </Row>
                </Skeleton>
              ),
            },
          ]}
        />
      </div>

      <Row gutter={[24, 24]} className="mb20 mx-0">
        <Col xs={24} lg={12} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Sales Pipeline Report"
            data={salesOrderByStatus
              ?.filter((i) => !!i?.typeValue)
              ?.map((d) => ({
                label: d?.typeValue,
                value: d?.countSum || 0,
                sentiment:
                  d?.countSum > 80
                    ? "High"
                    : d?.countSum > 50
                      ? "Medium"
                      : "Low",
                ...d,
              }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(
                      `/search-lead?Lead Status=${clickedData.statusId}`,
                    );
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} lg={12} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Top 5 Open Opportunities"
            data={openOpprtunityData?.map((d) => ({
              label: d?.fullname,
              value: parseFloat(d?.oppurtunityAmount) || 0,
              sentiment:
                d?.oppurtunityAmount > 200000
                  ? "High"
                  : d?.oppurtunityAmount > 100000
                    ? "Medium"
                    : "Low",
              ...d,
            }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(`/lead-details/${clickedData?.id}`);
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} lg={12} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Sales Trend"
            data={salesTrendWonDataByMonth?.map((d) => ({
              label: d?.month,
              value: d?.opportunitSum || 0,
            }))}
          />
        </Col>
        <Col xs={24} lg={12} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Top 5 Closed Opportunities"
            data={closedOpprtunityData
              ?.map((d) => ({
                name: d?.firstname + " " + d?.lastname,
                value: d?.oppurtunityAmount || 0,
                sentiment:
                  d?.oppurtunityAmount > 200000
                    ? "High"
                    : d?.oppurtunityAmount > 100000
                      ? "Medium"
                      : "Low",
                ...d,
              }))
              .sort((a, b) => b.value - a.value)
              ?.map((d) => ({
                label: d?.name,
                value: d?.value,
                ...d,
              }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(`/lead-details/${clickedData?.id}`);
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} className="!tw-px-0">
          <Card className="tw-border tw-border-border-secondary h-100">
            <div className="tw-text-base tw-font-semibold">
              Closed Sales (Actual vs Target)
            </div>

            <div className="mt30 tw-flex tw-justify-center">
              {isLoading ? (
                <div className="tw-w-full">
                  <Skeleton active paragraph={{ rows: 6 }} title={false} />
                </div>
              ) : (
                <div className="tw-w-full tw-max-w-[520px] closed-sales-graph">
                  <Gauge {...chart2} style={{ height: 180 }} />
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LeadReport;
